<template>
  <vs-dropdown
    v-if="enabled"
    ref="inboxDropdown"
    vs-custom-content
    vs-trigger-click
    class="cursor-pointer admin-inbox-dropdown-wrap"
    @focus="onInboxOpen"
  >
    <div
      class="admin-inbox-trigger"
      :class="'is-' + variant"
      role="button"
      tabindex="0"
      :aria-label="bellLabel"
    >
      <feather-icon icon="BellIcon" svgClasses="w-5 h-5 stroke-current" />
      <span v-if="variant !== 'icon'" class="admin-inbox-cta-label">Notifications</span>
      <span v-if="unreadCount" class="admin-inbox-badge">{{ unreadLabel }}</span>
    </div>

    <vs-dropdown-menu class="admin-inbox-menu vx-navbar-dropdown">
      <div class="admin-inbox-card">
        <div class="admin-inbox-header">
          <div>
            <h3>Notifications</h3>
            <p>{{ headerCopy }}</p>
          </div>
        </div>

        <div class="admin-inbox-tabs admin-inbox-tabs--paired">
          <button
            type="button"
            class="admin-inbox-tab"
            :class="{ 'is-active': activeTab === 'sarai' }"
            @mousedown.prevent.stop="activeTab = 'sarai'"
            @click.prevent.stop="activeTab = 'sarai'"
          >
            Sarai
            <span>{{ saraiItems.length }}</span>
          </button>
          <button
            type="button"
            class="admin-inbox-tab"
            :class="{ 'is-active': activeTab === 'service' }"
            @mousedown.prevent.stop="activeTab = 'service'"
            @click.prevent.stop="activeTab = 'service'"
          >
            Service
            <span>{{ serviceItems.length }}</span>
          </button>
          <button
            type="button"
            class="admin-inbox-tab"
            :class="{ 'is-active': activeTab === 'hotel' }"
            @mousedown.prevent.stop="activeTab = 'hotel'"
            @click.prevent.stop="activeTab = 'hotel'"
          >
            Hotel
            <span>{{ hotelItems.length }}</span>
          </button>
        </div>

        <div v-if="loading && !visibleItems.length" class="admin-inbox-empty">Loading notifications...</div>

        <ul v-else-if="tabItems.length" class="admin-inbox-list">
          <li
            v-for="item in tabItems"
            :key="item._id"
            class="admin-inbox-item"
            :class="[kindOf(item) === 'sarai' || kindOf(item) === 'hotel' ? 'is-sarai' : 'is-service', { 'is-unread': !item.is_read }]"
            @mousedown.prevent.stop="openNotification(item)"
            @click.prevent.stop="openNotification(item)"
          >
            <div class="admin-inbox-icon">
              <feather-icon :icon="iconFor(item)" svgClasses="w-4 h-4 stroke-current" />
            </div>
            <div class="admin-inbox-body">
              <div class="admin-inbox-row">
                <span class="admin-inbox-chip">{{ kindLabel(item) }}</span>
                <span class="admin-inbox-time">{{ relativeTime(item.createdAt) }}</span>
              </div>
              <span class="admin-inbox-title">{{ item.title || 'Booking notification' }}</span>
              <p v-if="item.details" class="admin-inbox-details">{{ item.details }}</p>
              <span v-if="gurudwaraName(item)" class="admin-inbox-place">{{ gurudwaraName(item) }}</span>
            </div>
          </li>
        </ul>

        <div v-else class="admin-inbox-empty">
          <p class="admin-inbox-empty-title">{{ activeTab === 'sarai' ? 'No sarai bookings' : (activeTab === 'hotel' ? 'No hotel bookings' : 'No service bookings') }}</p>
          <p class="admin-inbox-empty-copy">Mobile bookings for this tab will show up here.</p>
        </div>
      </div>
    </vs-dropdown-menu>
  </vs-dropdown>
</template>

<script>
import { mapState } from 'vuex'
import featureFlags from '@/config/featureFlags'

export default {
  name: 'notification-drop-down',
  props: {
    variant: {
      type: String,
      default: 'cta'
    }
  },
    data() {
    return {
      navigating: false,
      markingRead: false,
      activeTab: 'service'
    }
  },
  computed: {
    ...mapState('adminNotifications', ['items', 'unreadCount', 'defaultGurudwaraId', 'loading']),
    enabled() {
      return featureFlags.enableAdminInbox
    },
    visibleItems() {
      const bookingTypes = ['admin_sarai_booking', 'admin_hotel_booking', 'admin_service_booking']
      return (this.items || []).filter((item) => bookingTypes.includes(item.type) || item.kind)
    },
    saraiItems() {
      return this.visibleItems.filter((item) => this.kindOf(item) === 'sarai')
    },
    hotelItems() {
      return this.visibleItems.filter((item) => this.kindOf(item) === 'hotel')
    },
    serviceItems() {
      return this.visibleItems.filter((item) => this.kindOf(item) !== 'sarai' && this.kindOf(item) !== 'hotel')
    },
    tabItems() {
      if (this.activeTab === 'sarai') return this.saraiItems
      if (this.activeTab === 'hotel') return this.hotelItems
      return this.serviceItems
    },
    headerCopy() {
      const total = this.visibleItems.length
      if (!total && this.loading) return 'Checking bookings...'
      if (!total) return 'Hotel, sarai and service bookings'
      const unread = this.visibleItems.filter((item) => !item.is_read).length
      if (!unread) return `${total} booking notification${total === 1 ? '' : 's'}`
      return `${unread} new · ${total} booking notification${total === 1 ? '' : 's'}`
    },
    unreadLabel() {
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount)
    },
    bellLabel() {
      return this.unreadCount
        ? `Notifications, ${this.unreadCount} unread`
        : 'Notifications'
    }
  },
  created() {
    this.$store.dispatch('adminNotifications/fetchInbox')
  },
  methods: {
    async onInboxOpen(event) {
      if (event && event.type) return
      const dropdown = this.$refs.inboxDropdown
      if (!dropdown || !dropdown.vsDropdownVisible) return
      if (this.markingRead || !this.unreadCount) return
      this.markingRead = true
      try {
        await this.$store.dispatch('adminNotifications/markAllRead')
        await this.$store.dispatch('adminNotifications/fetchInbox')
      } catch (error) {
        await this.$store.dispatch('adminNotifications/fetchUnreadCount')
        this.$vs.notify({
          title: 'Notifications',
          text: 'Could not mark notifications as read. Please try again.',
          color: 'warning'
        })
      } finally {
        this.markingRead = false
      }
    },
    kindOf(item) {
      if (!item) return 'service'
      if (item.kind === 'hotel' || item.type === 'admin_hotel_booking') return 'hotel'
      if (item.kind === 'sarai' || item.type === 'admin_sarai_booking') return 'sarai'
      if (item.kind) return item.kind
      return 'service'
    },
    kindLabel(item) {
      const labels = {
        hotel: 'Hotel',
        sarai: 'Sarai',
        prasad: 'Prasad',
        anandKaraj: 'Anand Karaj',
        service: 'Service'
      }
      return labels[this.kindOf(item)] || 'Service'
    },
    iconFor(item) {
      return (this.kindOf(item) === 'sarai' || this.kindOf(item) === 'hotel') ? 'HomeIcon' : 'CalendarIcon'
    },
    gurudwaraName(item) {
      return item && item.gurudwara && item.gurudwara.name ? item.gurudwara.name : ''
    },
    refId(value) {
      if (!value) return null
      if (typeof value === 'object') return value._id || null
      return value
    },
    gurudwaraIdFrom(item) {
      if (!item) return this.defaultGurudwaraId
      if (item.gurudwara && item.gurudwara._id) return item.gurudwara._id
      return item.gurudwara || this.defaultGurudwaraId
    },
    resolveGurudwaraId() {
      if (this.defaultGurudwaraId) return this.defaultGurudwaraId
      const withGurudwara = (this.items || []).find((item) => this.gurudwaraIdFrom(item))
      if (withGurudwara) return this.gurudwaraIdFrom(withGurudwara)
      const records = this.$store.state.gurudwara && this.$store.state.gurudwara.records
      if (records && records[0] && records[0]._id) return records[0]._id
      return null
    },
    closeSidebarIfMobile() {
      if (this.$store.state.windowWidth < 1250) {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false)
      }
    },
    async navigateTo(kind, gurudwaraId, bookingId) {
      if (this.navigating) return
      this.navigating = true
      this.closeSidebarIfMobile()
      try {
        let id = gurudwaraId || this.resolveGurudwaraId()
        if (!id) {
          id = await this.$store.dispatch('adminNotifications/ensureDefaultGurudwaraId')
        }
        const query = { open: String(Date.now()) }
        if (bookingId) query.booking = String(bookingId)
        if (kind === 'hotel') {
          const path = id
            ? `/gurudwara/${id}/hotel/bookings`
            : '/hotel/bookings'
          await this.$router.push({ path, query }).catch(() => {})
          return
        }
        if (kind === 'sarai') {
          const path = id
            ? `/gurudwara/${id}/sarai/bookings`
            : '/gurudwara/sarai/bookings'
          await this.$router.push({ path, query }).catch(() => {})
          return
        }
        const path = id
          ? `/gurudwara/${id}/service/bookings`
          : '/gurudwara/service/bookings'
        await this.$router.push({ path, query }).catch(() => {})
      } finally {
        this.navigating = false
      }
    },
    async openNotification(item) {
      if (!item) return
      const kind = this.kindOf(item)
      let bookingId = null
      if (kind === 'hotel') {
        bookingId = this.refId(item.hotel_booking)
      } else if (kind === 'sarai') {
        bookingId = this.refId(item.sarai_booking)
      } else {
        bookingId = this.refId(item.booking)
      }
      await this.navigateTo(kind, this.gurudwaraIdFrom(item), bookingId)
    },
    relativeTime(value) {
      if (!value) return ''
      const start = new Date(value)
      const diffMs = Date.now() - start.getTime()
      const seconds = Math.max(0, Math.floor(diffMs / 1000))
      if (seconds < 60) return 'just now'
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ago`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}h ago`
      const days = Math.floor(hours / 24)
      if (days < 7) return `${days}d ago`
      const weeks = Math.floor(days / 7)
      if (weeks < 5) return `${weeks} week${weeks > 1 ? 's' : ''} ago`
      const months = Math.floor(days / 30)
      if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`
      const years = Math.floor(days / 365)
      return `${years} year${years > 1 ? 's' : ''} ago`
    }
  }
}
</script>

<style lang="scss">
.admin-inbox-dropdown-wrap {
  display: inline-flex;
  align-items: center;
  overflow: visible;
}

.admin-inbox-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-primary);
  cursor: pointer;
}

.admin-inbox-trigger.is-icon {
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  border-radius: 50%;
}

.admin-inbox-trigger.is-icon:hover {
  background: rgba(0, 0, 0, 0.06);
}

.theme-dark .admin-inbox-trigger.is-icon:hover {
  background: rgba(255, 255, 255, 0.08);
}

.admin-inbox-trigger.is-cta {
  min-height: 42px;
  padding: 10px 16px;
  border: 1px solid var(--text-primary);
  background: var(--page-bg);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

.admin-inbox-trigger.is-cta:hover {
  background: rgba(0, 0, 0, 0.04);
}

.theme-dark .admin-inbox-trigger.is-cta:hover {
  background: rgba(255, 255, 255, 0.06);
}

.admin-inbox-cta-label {
  line-height: 1;
}

@media (max-width: 640px) {
  .admin-inbox-cta-label {
    display: none;
  }
}

.admin-inbox-badge {
  position: absolute;
  top: -9px;
  right: -9px;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ea5455;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  box-shadow: none;
  z-index: 2;
  pointer-events: none;
}

.admin-inbox-menu {
  padding: 0 !important;
  overflow: hidden;
  z-index: 50000 !important;
}

.admin-inbox-card {
  width: 400px;
  max-width: calc(100vw - 24px);
  background: var(--page-bg);
  color: var(--text-primary);
}

.admin-inbox-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--divider);
}

.admin-inbox-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.admin-inbox-header p {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-secondary);
}

.admin-inbox-tabs {
  display: flex;
  gap: 8px;
  padding: 10px 12px 0;
}

.admin-inbox-tabs--paired .admin-inbox-tab:nth-child(1),
.admin-inbox-tabs--paired .admin-inbox-tab:nth-child(2) {
  flex: 1 1 calc(50% - 4px);
  min-width: 0;
}

.admin-inbox-tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid var(--divider);
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.admin-inbox-tab span {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  font-size: 11px;
  line-height: 18px;
}

.admin-inbox-tab.is-active {
  border-color: #1d4f91;
  background: #e8f1fb;
  color: #1d4f91;
}

.admin-inbox-tab.is-active span {
  background: #1d4f91;
  color: #fff;
}

.admin-inbox-list {
  list-style: none;
  margin: 0;
  padding: 6px;
  max-height: 440px;
  overflow-y: auto;
}

.admin-inbox-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 6px;
  padding: 10px 10px 10px 12px;
  border: 1px solid transparent;
  border-radius: 10px;
  cursor: pointer;
}

.admin-inbox-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.theme-dark .admin-inbox-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.admin-inbox-item.is-unread {
  background: rgba(17, 17, 17, 0.03);
  border-color: var(--divider);
}

.admin-inbox-item.is-unread.is-sarai {
  box-shadow: inset 3px 0 0 #b7791f;
}

.admin-inbox-item.is-unread.is-service {
  box-shadow: inset 3px 0 0 #2b6cb0;
}

.admin-inbox-icon {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  margin-top: 2px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.admin-inbox-item.is-sarai .admin-inbox-icon {
  background: #f8f1e3;
  color: #8a5a12;
}

.admin-inbox-item.is-service .admin-inbox-icon {
  background: #e8f1fb;
  color: #1d4f91;
}

.admin-inbox-body {
  min-width: 0;
  flex: 1;
}

.admin-inbox-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.admin-inbox-chip {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.admin-inbox-item.is-sarai .admin-inbox-chip {
  background: #f8f1e3;
  color: #8a5a12;
}

.admin-inbox-item.is-service .admin-inbox-chip {
  background: #e8f1fb;
  color: #1d4f91;
}

.admin-inbox-title {
  display: block;
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--text-primary);
}

.admin-inbox-item.is-unread .admin-inbox-title {
  font-weight: 700;
}

.admin-inbox-details {
  display: -webkit-box;
  margin: 3px 0 0;
  overflow: hidden;
  font-size: 13px;
  line-height: 1.4;
  color: var(--text-secondary);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.admin-inbox-place {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.admin-inbox-time {
  flex: 0 0 auto;
  font-size: 11px;
  color: var(--text-secondary);
}

.admin-inbox-empty {
  padding: 18px;
}

.admin-inbox-empty-title {
  margin: 0;
  font-weight: 700;
}

.admin-inbox-empty-copy {
  margin: 6px 0 14px;
  font-size: 13px;
  color: var(--text-secondary);
}

@media (max-width: 640px) {
  .admin-inbox-card {
    width: 380px;
    max-width: calc(100vw - 16px);
  }
}
</style>
